import React, { useEffect } from "react";
import { c2pa_init } from 'https://cdn.jsdelivr.net/gh/contentauth/dash.js@3ecdd3b/samples/c2pa/plugin-dash/c2pa-dash-plugin.js';
import { C2PAPlayer } from 'https://cdn.jsdelivr.net/gh/contentauth/dash.js@3ecdd3b/samples/c2pa/C2paPlayer/main.js';
import useLoadScripts from '../hooks/useLoadScripts';
import { brandBlue, textDark, border, white, background, container, sectionPadding, heroPadding, card } from '../styles/theme';

// const ORIGINAL_STREAM_URL = 'https://d1l4py166ep97a.cloudfront.net/c2pa/ausp_1_clean/dash/index.mpd';
// const TEMPERED_STREAM_URL = 'https://d1l4py166ep97a.cloudfront.net/c2pa/ausp_1_tempered/dash/index.mpd';
const ORIGINAL_STREAM_URL = 'https://d1l4py166ep97a.cloudfront.net/c2pa/cvk_clean/dash/index.mpd';
const TEMPERED_STREAM_URL = 'https://d1l4py166ep97a.cloudfront.net/c2pa/cvk_tempered/dash/index.mpd';

let was_initialized = false;

const C2pa = () => {
  const { loaded } = useLoadScripts([
    'https://vjs.zencdn.net/8.3.0/video.js',
    'https://cdnjs.cloudflare.com/ajax/libs/dashjs/4.7.2/dash.all.debug.min.js',
  ]);

  useEffect(() => {
    if (was_initialized) {
      window.location.reload()

      return;
    }
  }, []);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    const videoJsOptions = {
      fluid: true,
      autoplay: true,
      muted: true,
      inactivityTimeout: 0,
      controlBar: {
        children: [
          'playToggle',
          'progressControl',
          'currentTimeDisplay',
          'volumePanel',
          'pictureInPictureToggle',
          'fullscreenToggle',
          'liveDisplay',
        ]
      }
    };

    const {
      dashjs,
      videojs,
    } = window;

    console.log('>>', {
      c2pa_init,
      C2PAPlayer,
      dashjs,
      videojs,
    });
       
    // Initialize original content player
    const originalVideoElement = document.querySelector('#original-player');
    const originalDashPlayer = dashjs.MediaPlayer().create();
    const originalVideoJsPlayer = videojs('original-player', videoJsOptions);
    const originalC2paPlayer = new C2PAPlayer(originalVideoJsPlayer, originalVideoElement);
    originalC2paPlayer.initialize();
    
    // Initialize tempered content player
    const temperedVideoElement = document.querySelector('#tempered-player');
    const temperedDashPlayer = dashjs.MediaPlayer().create();
    const temperedVideoJsPlayer = videojs('tempered-player', videoJsOptions);
    const temperedC2paPlayer = new C2PAPlayer(temperedVideoJsPlayer, temperedVideoElement);
    temperedC2paPlayer.initialize();

    // Initialize C2PA for both players
    c2pa_init(originalDashPlayer, (e) => {
      return originalC2paPlayer.playbackUpdate(e.c2pa_status);
    }).then(() => {
      originalDashPlayer.initialize(originalVideoElement, ORIGINAL_STREAM_URL, true);
    });

    c2pa_init(temperedDashPlayer, (e) => {
      return temperedC2paPlayer.playbackUpdate(e.c2pa_status);
    }).then(() => {
      temperedDashPlayer.initialize(temperedVideoElement, TEMPERED_STREAM_URL, true);
    });

    was_initialized = true;
  }, [loaded]);

  return (
    <div className="min-h-screen bg-white" style={{ color: textDark }}>
      {/* Hero Section with Image on Right */}
      <section className={`mx-auto ${container} px-6 ${heroPadding}`}>
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <h1 className="text-3xl md:text-5xl font-bold leading-tight" style={{ color: brandBlue }}>
              C2PA Content Authentication
              <span className="block text-lg md:text-2xl mt-3 font-normal" style={{ color: textDark, opacity: 0.85 }}>
                Verify content provenance for VOD assets
              </span>
            </h1>
            <p className="mt-4 max-w-2xl" style={{ opacity: 0.9 }}>
              Ensure content authenticity and traceability with C2PA (Content Credentials for the Public Web). Our solution validates the provenance of VOD content, providing transparency and trust in an era of AI-generated media.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              {['Signed Streams', 'DASH/HLS/MP4', 'Standards Alignment', 'Flexible Integration'].map((t,i)=> (
                <span key={i} className="rounded-full px-3 py-1 border" style={{ borderColor: brandBlue, color: brandBlue }}>
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative rounded-2xl overflow-hidden shadow-lg" style={{ border: `1px solid ${border}` }}>
              <div className="aspect-video">
                <img src="/images/c2pa_poster.png" alt="Video Upscaling Poster" className="h-full w-full object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Comparison Players - 2 Columns */}
      <section id="demo-section" className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          C2PA Content Verification Demo
        </h2>
        <div className="grid md:grid-cols-2 gap-6 items-start">
          {/* Column 1: Original Content Player */}
          <div className="rounded-2xl overflow-hidden shadow-lg" style={{ border: `1px solid ${border}` }}>
            <div className="p-4" style={{ background: background, borderBottom: `1px solid ${border}` }}>
              <h3 className="font-semibold" style={{ color: brandBlue }}>Original Content</h3>
            </div>
            <div className="p-6">
              <link href="https://vjs.zencdn.net/8.3.0/video-js.css" rel="stylesheet" />
              <link href="https://cdn.jsdelivr.net/gh/contentauth/dash.js@3ecdd3b/samples/c2pa/c2pa-player.css" rel="stylesheet" />
              <div className="video-container">
                <video className="video-js w-full" id="original-player" autoPlay controls />
              </div>
            </div>
          </div>
          
          {/* Column 2: Tempered Content Player */}
          <div className="rounded-2xl overflow-hidden shadow-lg" style={{ border: `1px solid ${border}` }}>
            <div className="p-4" style={{ background: background, borderBottom: `1px solid ${border}` }}>
              <h3 className="font-semibold" style={{ color: brandBlue }}>Tempered Content</h3>
            </div>
            <div className="p-6">
              <link href="https://vjs.zencdn.net/8.3.0/video-js.css" rel="stylesheet" />
              <link href="https://cdn.jsdelivr.net/gh/contentauth/dash.js@3ecdd3b/samples/c2pa/c2pa-player.css" rel="stylesheet" />
              <div className="video-container">
                <video className="video-js w-full" id="tempered-player" autoPlay controls />
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Key Capabilities */}
      <section className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          C2PA: Key Capabilities
        </h2>
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <h3 className="text-lg font-semibold mb-4" style={{ color: brandBlue }}>How does C2PA transcoding solution work?</h3>
            <ul className="space-y-3 text-sm" style={{ color: textDark, opacity: 0.9 }}>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Signed Streams:</strong> The transcoder generates C2PA manifests that describe encoding and packaging steps. These manifests are cryptographically signed and embedded into the final packaged assets (MP4 or CMAF segments for DASH/HLS).                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Standards Alignment:</strong> The signing process follows the C2PA/Content Credentials specification, ensuring compatibility with emerging regulatory and platform requirements for content authenticity in 2025.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Flexible Integration:</strong> We seamlessly integrate with customer pipelines, ingesting their streams and outputting signed assets ready for distribution.</span>
              </li>
            </ul>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-2xl p-5" style={{ background: white, border: `1px solid ${border}`, boxShadow: card, color: textDark }}>
              <h3 className="font-semibold mb-3" style={{ color: brandBlue }}>Technical Advantages</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Industry-standard streaming outputs</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Cryptographically signed manifests</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Future-ready compliance</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Seamless pipeline integration</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Player-Side Innovation */}
      <section className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          Player-Side Innovation
        </h2>
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <h3 className="text-lg font-semibold mb-4" style={{ color: brandBlue }}>Enhanced Viewer Experience</h3>
            <ul className="space-y-3 text-sm" style={{ color: textDark, opacity: 0.9 }}>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Authenticity Badge:</strong> Our reference player surfaces a Content Credentials icon within the UI. Users can click or tap to view provenance details, such as who signed the content, when, and which tools were used.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Tamper Awareness:</strong> The player is capable of highlighting tampered segments directly in the progress bar, allowing viewers to see exactly which parts of the stream remain verified and which are untrusted. This makes authenticity a transparent part of the playback experience.</span>
              </li>
            </ul>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-2xl p-5" style={{ background: white, border: `1px solid ${border}`, boxShadow: card, color: textDark }}>
              <h3 className="font-semibold mb-3" style={{ color: brandBlue }}>User Benefits</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Transparent content verification</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Real-time tamper detection</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Detailed provenance information</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Enhanced viewer trust</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          Technical Features & Capabilities
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "Custom Transcoding Engine",
              badges: ["C2PA Manifest Generation", "Cryptographic Signing", "Asset Embedding"],
              desc: "In-house developed transcoder that generates and embeds C2PA manifests during the encoding and packaging process"
            },
            {
              title: "Industry Standard Outputs",
              badges: ["DASH (fMP4)", "HLS (fMP4)", "MP4 Containers"],
              desc: "Generates signed assets in DASH, HLS, and MP4 formats with embedded C2PA manifests ready for distribution"
            },
            {
              title: "Standards Compliance",
              badges: ["C2PA Specification", "Content Credentials", "2025 Ready"],
              desc: "Full compliance with C2PA/Content Credentials specification for future regulatory requirements"
            }
          ].map((feature, i) => (
            <article key={i} className="group rounded-2xl overflow-hidden transition" style={{ background: white, border: `1px solid ${border}`, boxShadow: card }}>
              <div className="p-5" style={{ color: textDark }}>
                <h3 className="font-medium mb-3" style={{ color: brandBlue }}>{feature.title}</h3>
                <p className="text-sm mb-3" style={{ opacity: 0.85 }}>{feature.desc}</p>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  {feature.badges.map((badge, j) => (
                    <span key={j} className="rounded-full px-2.5 py-1 border" style={{ borderColor: brandBlue, color: brandBlue }}>{badge}</span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Value to Customers */}
      <section className={`mx-auto ${container} px-6 ${sectionPadding}`}>
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-center mb-8" style={{ color: brandBlue }}>
          What do you gain?
        </h2>
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <h3 className="text-lg font-semibold mb-4" style={{ color: brandBlue }}>Strategic Advantages</h3>
            <ul className="space-y-3 text-sm" style={{ color: textDark, opacity: 0.9 }}>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Trust & Transparency:</strong> You deliver streaming content that is verifiably authentic.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Future-Proofing:</strong> As platforms and ecosystems adopt C2PA more broadly, you will already be prepared with compliant, signed video pipelines.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-2 h-2 w-2 rounded-full" style={{ background: brandBlue }} />
                <span><strong>Ease of Deployment:</strong> With our transcoder and player components, you won’t need to overhaul existing workflows-C2PA signing integrates seamlessly into the encoding and packaging pipeline, while verification happens during playback.                </span>
              </li>
            </ul>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-2xl p-5" style={{ background: white, border: `1px solid ${border}`, boxShadow: card, color: textDark }}>
              <h3 className="font-semibold mb-3" style={{ color: brandBlue }}>Key benefits</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>No workflow disruption</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Regulatory compliance ready</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Verifiable content authenticity</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full" style={{ background: brandBlue }} />
                  <span>Professional services support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6" style={{ background: brandBlue }}>
        <div className={`mx-auto ${container} text-center`}>
          <h2 className="text-3xl font-bold mb-4 text-white">
            Ready to Verify Your Content Authenticity?
          </h2>
          <p className="text-xl mb-8 text-white opacity-90">
            Implement C2PA content authentication to build trust and ensure content provenance. Our engineers will help you integrate this solution into your existing workflow.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a 
              href="https://www.bigbluemarble.com/en/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
              style={{ background: white, color: brandBlue, textDecoration: 'none' }}
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default C2pa;